import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import DeploymentSelector from './DeploymentSelector';
import EligibilityForm from './Form';
import Results from './CheckResults';
import Modal2 from 'components/_shared/Modal2';

import { Deployments, MemberSearchResult, User } from 'types';

import { makeStyles } from '@mui/styles';
import { ExtensionRequestKind, handleStartRegistration, IntakeMemberSearchResultType } from 'util/membershipHelper';
import ReenrollPopup from 'components/membership/ReenrollPopup';
import ReinstatePopup from 'components/membership/ReinstatePopup';
import ClosedMembershipPopup from 'components/membership/ClosedMembershipPopup';
import ExistingRestrictedMember from 'components/membership/ExistingRestrictedMember';
import useIntakeAutofill from 'hooks/useIntakeAutofill';
import ReinstateForm from 'components/membership/ReinstateForm';
import DirectReinstatePopup from 'components/membership/DirectReinstatePopup';

const useStyles = makeStyles(() => ({
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '32px'
    }
}));

type EligibilityPopUpProps = {
    handleCloseClick: () => void;
    user: User;
};

const iconStyle = {
    backgroundColor: '#E7EFFE',
    border: '2px solid #062D7A',
    color: '#062D7A',
    fontSize: '24px'
};

const getInitialFormCheck = () => ({
    dob: '',
    firstName: '',
    insuranceID: '',
    lastName: ''
});

const getInitialMemberState = () => ({
    memberInfo: null,
    memberFound: null
});

const getSearchParamsAndValues = (formCheck: {
    [key: string]: string;
}): [string[], string[]] => {
    const trimmedFormCheck = Object.fromEntries(
        Object.entries(formCheck).map(([key, value]) => [key, value.trim()])
    );
    const topFieldSearch =
        trimmedFormCheck.firstName &&
        trimmedFormCheck.lastName &&
        trimmedFormCheck.dob;
    const bottomFieldSearch = trimmedFormCheck.insuranceID;

    if (topFieldSearch && !bottomFieldSearch) {
        return [
            ['birthday', 'firstName', 'lastName'],
            [
                trimmedFormCheck.dob,
                trimmedFormCheck.firstName,
                trimmedFormCheck.lastName
            ]
        ];
    }

    if (!topFieldSearch && bottomFieldSearch) {
        return [['insuranceID'], [trimmedFormCheck.insuranceID]];
    }

    if (topFieldSearch && bottomFieldSearch) {
        return [
            ['birthday', 'firstName', 'lastName', 'insuranceID'],
            [
                trimmedFormCheck.dob,
                trimmedFormCheck.firstName,
                trimmedFormCheck.lastName,
                trimmedFormCheck.insuranceID
            ]
        ];
    }

    return [[], []];
};

const renderForm = ({
    selectedDeployment,
    formCheck,
    searchTerms,
    searchValues,
    member,
    setFormCheck,
    setMember,
    setSearchResultType,
    setUnavailableReason,
    setExtensionRequestKind,
    unavailableReason,
    handleCloseClick
}: {
    selectedDeployment: Deployments;
    formCheck: {
        dob: string;
        firstName: string;
        insuranceID: string;
        lastName: string;
    };
    searchTerms: string[];
    searchValues: string[];
    member: { memberInfo: MemberSearchResult | null; memberFound: boolean | null };
    setFormCheck: React.Dispatch<React.SetStateAction<any>>;
    setMember: React.Dispatch<React.SetStateAction<any>>;
    setSearchResultType: React.Dispatch<React.SetStateAction<IntakeMemberSearchResultType | null>>;
    setUnavailableReason: (reason: string | null) => void;
    setExtensionRequestKind: (kind: ExtensionRequestKind) => void;
    unavailableReason: string | null;
    handleCloseClick: () => void;
}) => {
    if (!member.memberInfo && member.memberFound === null) {
        return (
            <EligibilityForm
                selectedDeployment={selectedDeployment}
                formCheck={formCheck}
                searchTerms={searchTerms}
                searchValues={searchValues}
                setFormCheck={setFormCheck}
                setMember={setMember}
                setSearchResultType={setSearchResultType}
                setUnavailableReason={setUnavailableReason}
                setExtensionRequestKind={setExtensionRequestKind}
            />
        );
    }
    return (
        <Results
            formCheck={formCheck}
            handleCloseClick={handleCloseClick}
            member={member.memberInfo}
            selectedDeployment={selectedDeployment}
            searchTerms={searchTerms}
            unavailableReason={unavailableReason}
            setMember={setMember}
        />
    );
};

const renderResults = ({
    member,
    searchResultType,
    handleCloseClick,
    confirmedDeployment,
    history,
    autofillIntake,
    dispatch,
    showReinstateForm,
    setShowReinstateForm,
    unavailableReason,
    extensionRequestKind
}: {
    member: MemberSearchResult;
    searchResultType: IntakeMemberSearchResultType;
    handleCloseClick: () => void;
    confirmedDeployment: Deployments;
    history: ReturnType<typeof useHistory>;
    autofillIntake: (member: MemberSearchResult) => void;
    dispatch: ReturnType<typeof useDispatch>;
    showReinstateForm: boolean;
    setShowReinstateForm: (value: boolean) => void;
    unavailableReason: string | null;
    extensionRequestKind: ExtensionRequestKind;
}) => {
    return (
        <>
        {/* Reinstate Trigger */}
        {searchResultType === IntakeMemberSearchResultType.reinstateDirect && (
            <DirectReinstatePopup
                member={member}
                closeButtonText="Back to Homepage"
                onClose={handleCloseClick}
                onFinish={handleCloseClick}
            />
        )}
        {searchResultType === IntakeMemberSearchResultType.reinstate && !showReinstateForm && (
                <ReinstatePopup
                member={member}
                closeButtonText="Back to Homepage"
                handleCloseClick={handleCloseClick}
                handleReinstate={() => { 
                    setShowReinstateForm(true);
                }} 
            />
        )}
        {/* Reinstate Form */}
        {showReinstateForm && (
            <ReinstateForm
                member={member}
                deploymentName={confirmedDeployment?.name}
                requestKind={extensionRequestKind}
                closeButtonText="Back to Homepage"
                onClose={() => {
                    handleCloseClick();
                }}
                onFinish={() => {
                    handleCloseClick();
                }}
            />
        )}
        {/* Reenroll Trigger */}
        {searchResultType === IntakeMemberSearchResultType.reenroll && (
                <ReenrollPopup
                    member={member}
                    closeButtonText="Back to Homepage"
                    handleCloseClick={handleCloseClick}
                    handleReenroll={() => { 
                        handleCloseClick(); 
                        handleStartRegistration({ member, confirmedDeployment, history, autofillIntake, dispatch }); 
                        }
                    }
                />
            )}
        {searchResultType === IntakeMemberSearchResultType.closed && (
            <ClosedMembershipPopup
                member={member}
                reason={unavailableReason}
                closeButtonText="Back to Homepage"
                handleCloseClick={handleCloseClick}
            />
        )}
        {/* Active Restricted View */}
        {searchResultType === IntakeMemberSearchResultType.activeRestrictedView && (
            <ExistingRestrictedMember
                member={member}
                closeButtonText="Back to Homepage"
                handleCloseClick={handleCloseClick}
            />
        )}
    </>
  );
};

const EligibilityPopUp: React.FC<EligibilityPopUpProps> = ({
    handleCloseClick,
    user
}) => {
    const classes = useStyles();
    const history = useHistory();
    const { autofillIntake } = useIntakeAutofill();
    const dispatch = useDispatch();

    const [confirmedDeployment, setConfirmedDeployment] = useState<
        null | Deployments
    >(null);
    const [formCheck, setFormCheck] = useState<{
        dob: string;
        firstName: string;
        insuranceID: string;
        lastName: string;
    }>(getInitialFormCheck());
    const [member, setMember] = useState<{
        memberInfo: MemberSearchResult | null;
        memberFound: boolean | null;
    }>(getInitialMemberState());
    const [searchResultType, setSearchResultType] = useState<IntakeMemberSearchResultType | null>(null);
    const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
    const searchResultTypesToRenderSeperately = [IntakeMemberSearchResultType.reinstate, IntakeMemberSearchResultType.reinstateDirect, IntakeMemberSearchResultType.reenroll, IntakeMemberSearchResultType.closed, IntakeMemberSearchResultType.activeRestrictedView];

    const [searchTerms, searchValues] = getSearchParamsAndValues(formCheck);

    const [showReinstateForm, setShowReinstateForm] = useState(false);
    const [extensionRequestKind, setExtensionRequestKind] =
        useState<ExtensionRequestKind>('reinstatement');

    return (
        member.memberInfo && searchResultType && searchResultTypesToRenderSeperately.includes(searchResultType) ? (
            renderResults({
                member: member.memberInfo,
                searchResultType,
                handleCloseClick,
                confirmedDeployment: confirmedDeployment!, //assuming confirmedDeployment is not null since we are rendering results
                history,
                autofillIntake,
                dispatch,
                showReinstateForm,
                setShowReinstateForm,
                unavailableReason,
                extensionRequestKind
            })
        ) : (  
        <Modal2
            onClose={handleCloseClick}
            additionalStyles={{ iconStyle: iconStyle }}
            iconName={
                !member.memberInfo && member.memberFound === null
                    ? confirmedDeployment
                        ? 'fa-user-check'
                        : 'fa-user-plus'
                    : ''
            }
            title="Eligibility Checker"
        >
            <div className={classes.body}>
                {!confirmedDeployment ? (
                    <DeploymentSelector
                        setConfirmedDeployment={setConfirmedDeployment}
                        user={user}
                    />
                ) : (
                    renderForm({
                        selectedDeployment: confirmedDeployment!,
                        formCheck,
                        searchTerms,
                        searchValues,
                        member,
                        setFormCheck,
                        setMember,
                        handleCloseClick,
                        setSearchResultType,
                        setUnavailableReason,
                        setExtensionRequestKind,
                        unavailableReason
                    })
                )}
            </div>
        </Modal2>
    ))
};

export default EligibilityPopUp;
